import { Injectable } from '@angular/core';
import { BaseService } from './base.service';
import { map, Observable } from 'rxjs';
import { ApiResp } from '@app/types/global.type';
import { PeppolInfo } from '@app/types/taxpayer.type';

export interface Name {
  name: string | null;
  language: string | null;
}

export interface AdditionalIdentifier {
  identifierCode: string;
  identifierValue: string;
  scheme: string;
}

export interface WebsiteURI {
  websiteURI: string[] | null;
}

export interface Contact {
  type: string | null;
  name: string | null;
  phoneNumber: string | null;
  email: string | null;
}

export interface BusinessEntity {
  countryCode: string | null;
  geographicalInformation: string | null;
  names: { name: Name[] };
  websiteURIs?: WebsiteURI;
  contacts?: { contact: Contact[] };
  additionalInformation: string | null;
  additionalIdentifiers?: { additionalIdentifier: AdditionalIdentifier[] };
}

export interface ParticipantIdentifier {
  identifierCode: string | null;
  specialIdentifier?: string | null;
  businessIdentifier?: string | null;
  identifierValue?: string | null;
  scheme?: string;
}

export interface AccessPointConfiguration {
  endpointId: number;
  metadataProfileIds: { profileId: number[] };
}

interface KYC {
  method: string[];
  state: string;
  signed: boolean;
}

export interface AddUpdateParticipant {
  meta: {
    participantIdentifier: ParticipantIdentifier;
    smlActivation: boolean | null;
    pdActivation: boolean | null;
  };
  businessCard: { businessEntity: BusinessEntity[] };
  accessPointConfigurations: { accessPointConfiguration: AccessPointConfiguration[] };
}

interface Meta {
  participantIdentifier: ParticipantIdentifier;
  smlActivation: boolean;
  registrationDate: string;
  pdActivation: boolean;
}

export interface ParticipantData {
  meta: Meta;
  businessCard: {
    businessEntity: BusinessEntity[];
  };
  accessPointConfigurations: { accessPointConfiguration: AccessPointConfiguration[] };
  kyc: KYC;
}

@Injectable({
  providedIn: 'root',
})
export class PeppolService extends BaseService {
  addParticipant(payload: AddUpdateParticipant, params: { tin: string }): Observable<ApiResp> {
    return this.httpClient
      .post<ApiResp>(`/myportal/api/smp/participant`, payload, { params })
      .pipe(map((res) => this.handleFormResp(res)));
  }

  updateParticipant(payload: AddUpdateParticipant, params: { tin: string }): Observable<ApiResp> {
    return this.httpClient
      .put<ApiResp>(`/myportal/api/smp/participant`, payload, { params })
      .pipe(map((res) => this.handleFormResp(res)));
  }

  deleteParticipant(params: { tin: string; participantId: string }): Observable<ApiResp> {
    return this.httpClient
      .delete<ApiResp>(`/myportal/api/smp/participant`, { params })
      .pipe(map((res) => this.handleFormResp(res)));
  }

  getSmpParticipant(params: { tin: string; participantId: string }): Observable<ApiResp> {
    return this.httpClient.get<ApiResp>(`/myportal/api/smp/participant`, { params }).pipe(
      map((resp) => {
        if (!resp.status) this.toastr.error(resp.fieldErrors?.[0].message ?? resp.message);
        return resp;
      }),
    );
  }

  uploadKyc(params: { tin: string; participantId: string }, payload: File): Observable<ApiResp> {
    const file = new FormData();
    file.append('file', payload);

    return this.httpClient
      .post<ApiResp>(`/myportal/api/smp/kyc/upload`, file, { params })
      .pipe(map((res) => this.handleFormResp(res)));
  }

  verifyPepolId(params: { pid: string }): Observable<ApiResp> {
    return this.httpClient
      .get<ApiResp>('/myportal/pepol/verify/id', { params })
      .pipe(map((res) => this.handleFormResp(res)));
  }

  getPeppolInfo(id: string): Observable<PeppolInfo> {
    return this.httpClient.get<ApiResp>('/myportal/pepol/einvoice/detail/' + id).pipe(
      map((resp) => {
        if (!resp.status) this.toastr.error(resp.message, 'Error');
        return resp.status ? resp.response : null;
      }),
    );
  }
}
